import React, { useState, useEffect, useRef } from "react";
import AppLauncherButton from "./AppLauncherButton";
import { recordsApps, receiptsApps } from "../components/Constants";
import { useUser } from "../contexts/UserContext";
import { useAdminContext } from "../contexts/AdminContext";

const AppLauncherMenu = () => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef();
  const { userData } = useUser();

  const { adminData, isViewingOtherUser } = useAdminContext();

  const curUser = isViewingOtherUser ? adminData.userClientData : userData;

  // Close menu if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  // Permission check functions (same as NavigationPage)
  const hasReceiptsPermission = () => {
    if (!curUser?.permissions?.invoiceSystem?.receipts) return false;

    const { receipts } = curUser.permissions.invoiceSystem;
    return (
      receipts.invoicesSubAccess ||
      receipts.noticeChargesAccess ||
      receipts.ackReceiptsAccess
    );
  };

  const hasRecordsPermission = () => {
    if (!curUser?.permissions?.invoiceSystem?.records) return false;

    const { records } = curUser.permissions.invoiceSystem;

    // Check internal permissions
    const hasInternal =
      records.internal?.caseBriefAccess ||
      records.internal?.executiveAdminAccess ||
      records.internal?.secretarialLogsAccess ||
      records.internal?.processServerLogsAccess;

    // Check external permissions
    const hasExternal =
      records.external?.transmittalAccess ||
      records.external?.legalAdviceAccess ||
      records.external?.secretariatResolutionsAccess;

    return hasInternal || hasExternal;
  };

  // Check permissions for specific app based on its requirements
  const isAppAccessible = (app) => {
    // If app is already disabled, it's not accessible
    if (app.disabled) return false;

    // If no user data or permissions, nothing is accessible
    if (!curUser?.permissions?.invoiceSystem) return false;

    const { invoiceSystem } = curUser.permissions;

    // Check specific app permissions based on app name
    switch (app.name) {
      // Receipts Apps
      case "Invoices":
        return invoiceSystem.receipts?.invoicesSubAccess;

      case "Notice of Charges":
        return invoiceSystem.receipts?.noticeChargesAccess;

      case "Acknowledgement Receipts":
        return invoiceSystem.receipts?.ackReceiptsAccess;

      // External Records Apps
      case "Transmittal":
        return invoiceSystem.records?.external?.transmittalAccess;

      case "Legal Advice":
        return invoiceSystem.records?.external?.legalAdviceAccess;

      case "Secretariat Resolutions":
        return invoiceSystem.records?.external?.secretariatResolutionsAccess;

      // Internal Records Apps
      case "Case Brief":
        return invoiceSystem.records?.internal?.caseBriefAccess;

      case "Executive & Administrative":
        return invoiceSystem.records?.internal?.executiveAdminAccess;

      case "Secretarial Records":
        return invoiceSystem.records?.internal?.secretarialLogsAccess;

      case "Process Server & Messenger Logs":
        return invoiceSystem.records?.internal?.processServerLogsAccess;

      default:
        // If app name doesn't match, assume not accessible
        return false;
    }
  };

  // Filter apps based on permissions
  const accessibleReceiptsApps = receiptsApps.filter(isAppAccessible);
  const accessibleRecordsApps = recordsApps.filter(isAppAccessible);

  const externalApps = accessibleRecordsApps.filter(
    (app) => app.category === "External"
  );
  const internalApps = accessibleRecordsApps.filter(
    (app) => app.category === "Internal"
  );

  // Check if we have any external or internal records access
  const hasExternalAccess =
    curUser?.permissions?.invoiceSystem?.records?.external &&
    (curUser.permissions.invoiceSystem.records.external.transmittalAccess ||
      curUser.permissions.invoiceSystem.records.external.legalAdviceAccess ||
      curUser.permissions.invoiceSystem.records.external
        .secretariatResolutionsAccess);

  const hasInternalAccess =
    curUser?.permissions?.invoiceSystem?.records?.internal &&
    (curUser.permissions.invoiceSystem.records.internal.caseBriefAccess ||
      curUser.permissions.invoiceSystem.records.internal.executiveAdminAccess ||
      curUser.permissions.invoiceSystem.records.internal
        .secretarialLogsAccess ||
      curUser.permissions.invoiceSystem.records.internal
        .processServerLogsAccess);

  return (
    <div className="relative inline-block">
      {/* Toggle button */}
      <button
        onClick={() => setOpen(!open)}
        className="px-2 pt-2 rounded-md bg-tier-secondary hover:bg-dark-alt text-tier-primary"
        aria-label="Toggle App Launcher"
      >
        <span className="material-symbols-outlined">apps</span>
      </button>

      {/* Floating menu */}
      {open && (
        <div
          ref={menuRef}
          className="absolute right-0 mt-2 sm:w-96 w-80 bg-tier-primary dark:bg-tier-primary-dark border border-gray-200 dark:border-gray-600 rounded-xl p-4 shadow-2xl z-20"
        >
          {/* Receipts Section - Only show if user has receipts permission */}
          {hasReceiptsPermission() && accessibleReceiptsApps.length > 0 && (
            <section className="mb-6 bg-white/5 p-4 rounded">
              <h3 className="text-white font-semibold mb-4">Receipts</h3>
              <div className="grid grid-cols-4 gap-4 justify-items-center">
                {accessibleReceiptsApps.map((app, idx) => {
                  const { key, ...appProps } = app;
                  return (
                    <AppLauncherButton
                      key={idx}
                      {...appProps}
                      onClick={() => setOpen(false)}
                    />
                  );
                })}
              </div>
            </section>
          )}

          {/* Records Section - Only show if user has records permission */}
          {hasRecordsPermission() && (
            <section className="bg-white/5 p-4 rounded">
              <h3 className="text-white font-semibold mb-4">Records</h3>

              {/* External - Only show if user has external access and there are external apps */}
              {hasExternalAccess && externalApps.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs text-alt-color font-stretch-extra-condensed uppercase mb-2 ml-1">
                    External
                  </h4>
                  <div className="grid grid-cols-4 gap-4 justify-items-center">
                    {externalApps.map((app, idx) => {
                      const { key, ...appProps } = app;
                      return (
                        <AppLauncherButton
                          key={idx}
                          {...appProps}
                          onClick={() => setOpen(false)}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Internal - Only show if user has internal access and there are internal apps */}
              {hasInternalAccess && internalApps.length > 0 && (
                <div>
                  <h4 className="text-sm text-alt-color italic mb-2 ml-1">
                    Internal
                  </h4>
                  <div className="grid grid-cols-4 gap-4 justify-items-center">
                    {internalApps.map((app, idx) => {
                      const { key, ...appProps } = app;
                      return (
                        <AppLauncherButton
                          key={idx}
                          {...appProps}
                          onClick={() => setOpen(false)}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* No Access Message - Show if user has no access to any apps */}
          {!hasReceiptsPermission() && !hasRecordsPermission() && (
            <div className="flex flex-col items-center justify-center py-8">
              <span className="material-symbols-outlined text-4xl text-gray-400 mb-2">
                no_accounts
              </span>
              <p className="text-gray-500 text-center">
                No accessible applications
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AppLauncherMenu;
